// biome-ignore-all lint/style/useNamingConvention: Borg stream fields are snake_case
import type { BorgStreamEvent, BorgStreamEventMetadata } from "../models/borg-stream-types";
import type { BorgContentPart, BorgMessage, BorgToolStatus } from "../models/borg-types";

export type StreamEventAction = "update" | "clear" | "snapshot" | "done" | "none";

export interface StreamEventState {
	parts: Array<BorgContentPart>;
	messageId: string | undefined;
	lastSequence: number | undefined;
}

export interface StreamEventReduction {
	parts: Array<BorgContentPart>;
	messageId: string | undefined;
	lastSequence: number | undefined;
	action: StreamEventAction;
	hasSequenceGap: boolean;
	previousSequence: number | undefined;
	nextSequence: number | undefined;
	isMessageBoundary: boolean;
	snapshotMessage: BorgMessage | undefined;
}

interface EventTransitionResult {
	parts: Array<BorgContentPart>;
	action: StreamEventAction;
	snapshotMessage?: BorgMessage;
}

/** Build a new BorgMessage from the current parts and previous state. */
export function buildMessage(
	parts: Array<BorgContentPart>,
	prev: BorgMessage | undefined,
	messageId?: string,
): BorgMessage {
	const resolvedMessageId = messageId ?? prev?.id ?? "";
	const isSameMessage = prev != null && resolvedMessageId.length > 0 && prev.id === resolvedMessageId;
	const createdAt = isSameMessage ? prev.created_at : Date.now();

	return {
		id: resolvedMessageId,
		role: prev?.role ?? "assistant",
		parts,
		agent_type: prev?.agent_type,
		created_at: createdAt,
	};
}

const getEventMessageId = (event: BorgStreamEvent): string | undefined =>
	"message_id" in event && typeof event.message_id === "string" && event.message_id !== ""
		? event.message_id
		: undefined;

const buildFallbackPartID = (messageId: string | undefined, nextIndex: number): string | undefined =>
	messageId == null || messageId === "" ? undefined : `${messageId}:${nextIndex}`;

/** Append a text delta to the parts array, merging with the last text part if possible. */
function applyTextDelta(
	parts: Array<BorgContentPart>,
	content: string,
	messageId: string | undefined,
	partId: string | undefined,
): Array<BorgContentPart> {
	const last = parts.at(-1);
	const canMergeIntoLast = last?.type === "text" && (partId == null || partId === "" || last.part_id === partId);
	if (canMergeIntoLast) {
		const updated = { ...last, content: (last.content ?? "") + content };
		return [...parts.slice(0, -1), updated];
	}
	return [
		...parts,
		{ type: "text" as const, part_id: partId ?? buildFallbackPartID(messageId, parts.length + 1), content },
	];
}

/** Append a thinking delta to the parts array, merging with the last thinking part if possible. */
function applyThinkingDelta(
	parts: Array<BorgContentPart>,
	content: string,
	messageId: string | undefined,
	partId: string | undefined,
): Array<BorgContentPart> {
	const last = parts.at(-1);
	const canMergeIntoLast = last?.type === "thinking" && (partId == null || partId === "" || last.part_id === partId);
	if (canMergeIntoLast) {
		const updated = { ...last, content: (last.content ?? "") + content };
		return [...parts.slice(0, -1), updated];
	}
	return [
		...parts,
		{ type: "thinking" as const, part_id: partId ?? buildFallbackPartID(messageId, parts.length + 1), content },
	];
}

/** Update a tool call part's status by tool_use_id. */
interface ToolCallCompletion {
	partId: string | undefined;
	toolUseId: string;
	status: BorgToolStatus;
	durationMs: number | undefined;
}

function applyToolCallComplete(parts: Array<BorgContentPart>, completion: ToolCallCompletion): Array<BorgContentPart> {
	const { partId, toolUseId, status, durationMs } = completion;
	const idxByPartID =
		partId == null || partId === "" ? -1 : parts.findIndex((p) => p.type === "tool_call" && p.part_id === partId);
	const idx =
		idxByPartID >= 0 ? idxByPartID : parts.findIndex((p) => p.type === "tool_call" && p.tool_use_id === toolUseId);
	if (idx < 0) {
		return parts;
	}
	const existing = parts[idx];
	if (existing.type === "tool_call") {
		const updated = {
			type: "tool_call" as const,
			part_id: existing.part_id,
			tool_name: existing.tool_name,
			tool_use_id: existing.tool_use_id,
			tool_args: existing.tool_args,
			tool_status: status,
			duration_ms: durationMs,
		};
		return [...parts.slice(0, idx), updated, ...parts.slice(idx + 1)];
	}
	return [...parts];
}

/** Apply a stream event to the parts array and return the updated parts. */
function applyEventTransition(
	event: BorgStreamEvent,
	parts: Array<BorgContentPart>,
	messageId: string | undefined,
): EventTransitionResult {
	switch (event.type) {
		case "text_delta":
			return { parts: applyTextDelta(parts, event.content, messageId, event.part_id), action: "update" };
		case "thinking_delta":
			return { parts: applyThinkingDelta(parts, event.content, messageId, event.part_id), action: "update" };
		case "tool_call_start":
			return {
				parts: [
					...parts,
					{
						type: "tool_call",
						part_id: event.part_id ?? buildFallbackPartID(messageId, parts.length + 1),
						tool_name: event.tool_name,
						tool_use_id: event.tool_use_id,
						tool_args: event.tool_args,
						tool_status: "pending" as const,
					},
				],
				action: "update",
			};
		case "tool_call_complete":
			return {
				parts: applyToolCallComplete(parts, {
					partId: event.part_id,
					toolUseId: event.tool_use_id,
					status: event.status,
					durationMs: event.duration_ms,
				}),
				action: "update",
			};
		case "stream_reset":
			return { parts: [], action: "clear" };
		case "progress":
			return {
				parts: [
					...parts,
					{
						type: "progress",
						part_id: event.part_id ?? buildFallbackPartID(messageId, parts.length + 1),
						content: event.content,
					},
				],
				action: "update",
			};
		case "snapshot":
			return {
				parts: [...(event.current_message?.parts ?? [])],
				action: "snapshot",
				snapshotMessage: event.current_message,
			};
		case "agent_handover":
		case "message_complete":
			return { parts, action: "none" };
		case "error_event":
		case "done":
			return { parts, action: "done" };
	}
}

export function reduceStreamEvent(
	state: StreamEventState,
	event: BorgStreamEvent,
	metadata?: BorgStreamEventMetadata,
): StreamEventReduction {
	const incomingMessageId = getEventMessageId(event);
	const isMessageBoundary =
		incomingMessageId != null &&
		state.messageId != null &&
		state.messageId !== "" &&
		incomingMessageId !== state.messageId;
	const baseParts = isMessageBoundary ? [] : state.parts;
	const messageId = incomingMessageId ?? state.messageId;
	const previousSequence = state.lastSequence;
	const nextSequence = metadata?.sequence;
	const hasSequenceGap = nextSequence != null && previousSequence != null && nextSequence > previousSequence + 1;
	const lastSequence = nextSequence ?? state.lastSequence;

	if (hasSequenceGap) {
		return {
			parts: baseParts,
			messageId,
			lastSequence,
			action: "none",
			hasSequenceGap: true,
			previousSequence,
			nextSequence,
			isMessageBoundary,
			snapshotMessage: undefined,
		};
	}

	const transition = applyEventTransition(event, baseParts, messageId);
	const nextMessageId = transition.snapshotMessage?.id ?? messageId;

	return {
		parts: transition.parts,
		messageId: nextMessageId,
		lastSequence,
		action: transition.action,
		hasSequenceGap: false,
		previousSequence,
		nextSequence,
		isMessageBoundary,
		snapshotMessage: transition.snapshotMessage,
	};
}
